import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutValues, team, stats, contactInfo } from "@/mocks/site";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function About() {
  useDocumentMeta(
    "关于我们 - 51动漫",
    "了解 51动漫的平台定位、品牌理念与团队。我们致力于为动漫爱好者提供丰富的动漫资源与最新资讯服务。"
  );

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20cinematic%20anime%20backdrop%20of%20a%20cozy%20studio%20filled%20with%20artwork%20and%20glowing%20screens%2C%20orange%20and%20cream%20tones%2C%20creative%20atmosphere%2C%20wide%20banner&width=1600&height=700&seq=about-hero-01&orientation=landscape"
          alt="51动漫 平台介绍"
          title="关于 51动漫"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/65 to-foreground-950/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8 max-w-3xl">
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-background-50 mb-4">
              关于 51动漫
            </h1>
            <p className="text-background-100/85 text-sm md:text-lg leading-relaxed">
              51动漫是一个为动漫爱好者打造的动漫内容平台，我们聚合海量动漫作品信息与行业资讯，让每一次浏览都更省心、更过瘾。
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        {/* Positioning */}
        <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-4">
              我们的定位与发展
            </h2>
            <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground-700">
              <p>
                51动漫诞生于一群资深动漫爱好者的共同想法：做一个真正好用的动漫信息平台。我们希望用户不用在繁杂的信息里反复翻找，就能快速了解一部作品的类型、地区、年份、评分，以及它是否值得一看。
              </p>
              <p>
                从最初的作品收录，到现在覆盖行业动态、新番情报与作品评测的资讯频道，我们始终围绕「丰富动漫资源与资讯服务」这一核心，持续打磨内容与体验。未来，我们计划引入更多个性化推荐与社区互动能力，让每一位爱好者都能在这里找到属于自己的同好与热爱。
              </p>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=flat%20illustration%20of%20a%20team%20of%20creators%20working%20together%20surrounded%20by%20comic%20panels%20and%20screens%2C%20warm%20orange%20and%20cream%20palette%2C%20friendly%20editorial%20scene&width=1000&height=750&seq=about-story-01&orientation=landscape"
              alt="51动漫 团队工作场景"
              title="51动漫 内容团队"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </section>

        {/* Values */}
        <section className="mt-14 md:mt-20">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-6 md:mb-8 text-center">
            品牌理念
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {aboutValues.map((value) => (
              <div
                key={value.title}
                className="rounded-lg bg-background-100 border border-background-200 p-6"
              >
                <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${value.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="mt-12 md:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 p-6 md:p-8 rounded-lg bg-primary-950">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-heading font-extrabold text-2xl md:text-4xl text-primary-400">
                {stat.value}
              </div>
              <div className="mt-1.5 text-xs md:text-sm text-background-100/80">{stat.label}</div>
            </div>
          ))}
        </section>

        {/* Team */}
        <section className="mt-14 md:mt-20">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
            团队介绍
          </h2>
          <p className="text-center text-sm md:text-base text-foreground-500 mb-8 max-w-xl mx-auto">
            一群热爱动漫、也热爱把内容做好的人
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {team.map((member) => (
              <div
                key={member.name}
                className="rounded-lg bg-background-50 border border-background-200 p-5 text-center"
              >
                <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-background-200 mb-3">
                  <img
                    src={member.avatar}
                    alt={`${member.name} 头像`}
                    title={`${member.name} - 51动漫团队`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h3 className="font-heading font-bold text-base text-foreground-950">
                  {member.name}
                </h3>
                <p className="text-xs text-secondary-700 mt-1">{member.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Legal */}
        <section className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div id="copyright" className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500"></i>
              版权声明
            </h2>
            <p className="text-sm leading-relaxed text-foreground-600">
              本站所有内容均为平台演示数据，仅用于产品展示与学习交流，不提供任何动漫作品的在线播放与下载服务。若涉及版权问题，请通过
              <a href={`mailto:${contactInfo.email}`} className="text-primary-600 hover:underline mx-1">
                {contactInfo.email}
              </a>
              与我们联系，我们将及时处理。
            </p>
          </div>
          <div id="privacy" className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-shield-user-line text-primary-500"></i>
              隐私政策
            </h2>
            <p className="text-sm leading-relaxed text-foreground-600">
              我们重视你的隐私。本站的收藏功能仅使用浏览器本地存储记录，不会上传服务器；留言信息仅用于回复你的咨询。我们不会将你的个人信息用于任何未经授权的用途。
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-14 md:mt-20 rounded-lg bg-primary-500 p-8 md:p-12 text-center">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-background-50 mb-3">
            想和我们聊聊？
          </h2>
          <p className="text-background-100/90 text-sm md:text-base mb-6 max-w-xl mx-auto">
            无论是内容建议、合作洽谈，还是想加入我们，都欢迎随时联系。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50 text-primary-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
            联系我们
          </Link>
        </section>
      </div>
    </div>
  );
}